import React, { useEffect, useRef, useState, useCallback } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { sectionBadge, scrambleText } from "../../utils/animationUtils";
import { PiGlobe, PiPaintBrush, PiDatabase, PiPlugsConnected, PiHardDrives } from 'react-icons/pi';

gsap.registerPlugin(ScrollTrigger);

// ─── Data ─────────────────────────────────────────────────────────────────────
const servicesData = [
    {
        num: "01",
        icon: <PiGlobe weight="bold" />,
        text: "Web Development",
        desc: "Building scalable, high-performance web applications tailored to your business needs — from landing pages to complex enterprise platforms.",
        tags: ["React", "Next.js", "Laravel"],
        accent: "#FFE600",
    },
    {
        num: "02",
        icon: <PiPaintBrush weight="bold" />,
        text: "UI/UX Design",
        desc: "Crafting intuitive and visually stunning user interfaces that elevate user experience and drive measurable business outcomes.",
        tags: ["Figma", "Tailwind CSS", "Motion"],
        accent: "#FF3366",
    },
    {
        num: "03",
        icon: <PiDatabase weight="bold" />,
        text: "Database Management",
        desc: "Designing secure, efficient database architectures and implementing robust data management strategies for optimal performance.",
        tags: ["MySQL", "PostgreSQL", "Prisma"],
        accent: "#AAFF00",
    },
    {
        num: "04",
        icon: <PiPlugsConnected weight="bold" />,
        text: "System Integration",
        desc: "Seamlessly connecting diverse systems and APIs to streamline your business operations and eliminate workflow bottlenecks.",
        tags: ["REST API", "Node.js", "Webhooks"],
        accent: "#00C3FF",
    },
    {
        num: "05",
        icon: <PiHardDrives weight="bold" />,
        text: "Server & Infrastructure",
        desc: "Production server setup, maintenance, and optimization. VPS deployment on Linux with Nginx, SSL certificates, and automated backups for 99.9% uptime.",
        tags: ["Linux", "Nginx", "VPS"],
        accent: "#FF8C00",
    },
];

// ─── Single accordion row ──────────────────────────────────────────────────────
const ServiceRow = ({ service, index, isOpen, onToggle }) => {
    const rowRef       = useRef(null);
    const bodyRef      = useRef(null);
    const arrowRef     = useRef(null);
    const numRef       = useRef(null);
    const accentBarRef = useRef(null);
    const prevOpen     = useRef(false);

    // Animate body open/close whenever isOpen changes
    useEffect(() => {
        if (!bodyRef.current) return;

        if (isOpen && !prevOpen.current) {
            // ── open ──────────────────────────────────────────────────────────
            gsap.set(bodyRef.current, { height: "auto", opacity: 1 });
            const fullH = bodyRef.current.scrollHeight;
            gsap.fromTo(
                bodyRef.current,
                { height: 0, opacity: 0 },
                { height: fullH, opacity: 1, duration: 0.45, ease: "power3.out" }
            );
            // arrow spins to ↓ + bg turns dark
            gsap.to(arrowRef.current, { rotation: 90, backgroundColor: '#111', duration: 0.3, ease: "power3.out" });
            // SVG stroke → yellow
            const svgPath = arrowRef.current?.querySelector('path');
            if (svgPath) gsap.to(svgPath, { stroke: '#FFE600', duration: 0.2, ease: 'power2.out' });
            // ghost number fades in
            gsap.to(numRef.current, { opacity: 0.07, duration: 0.4, ease: "power2.out" });
            // accent bar slides in
            gsap.to(accentBarRef.current, { scaleY: 1, duration: 0.35, ease: "power3.out" });
            // row background tint
            gsap.to(rowRef.current, { backgroundColor: "#fefdfb", duration: 0.25 });

        } else if (!isOpen && prevOpen.current) {
            // ── close ─────────────────────────────────────────────────────────
            gsap.to(bodyRef.current, { height: 0, opacity: 0, duration: 0.35, ease: "power3.in" });
            gsap.to(arrowRef.current, { rotation: 0, backgroundColor: 'transparent', duration: 0.25, ease: "power3.in" });
            const svgPath = arrowRef.current?.querySelector('path');
            if (svgPath) gsap.to(svgPath, { stroke: '#111', duration: 0.2, ease: 'power2.out' });
            gsap.to(numRef.current, { opacity: 0, duration: 0.25 });
            gsap.to(accentBarRef.current, { scaleY: 0, duration: 0.3, ease: "power3.in" });
            gsap.to(rowRef.current, { backgroundColor: "transparent", duration: 0.25 });
        }

        prevOpen.current = isOpen;
    }, [isOpen]);

    // Hover – subtle row highlight (only when closed)
    const handleMouseEnter = useCallback(() => {
        if (isOpen) return;
        gsap.to(rowRef.current, { backgroundColor: "rgba(255,255,255,0.6)", duration: 0.2 });
        gsap.to(numRef.current, { opacity: 0.04, duration: 0.3 });
        gsap.to(arrowRef.current, { scale: 1.1, duration: 0.2, ease: 'back.out(2)' });
    }, [isOpen]);

    const handleMouseLeave = useCallback(() => {
        if (isOpen) return;
        gsap.to(rowRef.current, { backgroundColor: "transparent", duration: 0.2 });
        gsap.to(numRef.current, { opacity: 0, duration: 0.25 });
        gsap.to(arrowRef.current, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });
    }, [isOpen]);

    return (
        <div
            className="svc-row"
            ref={rowRef}
            style={{
                position: "relative",
                borderBottom: "2.5px solid #111",
                cursor: "pointer",
                overflow: "hidden",
                // transparent by default, GSAP handles tinting
                backgroundColor: "transparent",
                transition: "background-color 0.15s",
            }}
            onClick={onToggle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* ── Left accent bar (scaleY from bottom on open) ─────────── */}
            <div
                ref={accentBarRef}
                style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 5,
                    backgroundColor: service.accent,
                    transformOrigin: "bottom",
                    transform: "scaleY(0)",
                    borderRadius: "0 3px 3px 0",
                    zIndex: 1,
                }}
            />

            {/* ── Ghost number watermark (right side) ──────────────────── */}
            <span
                ref={numRef}
                style={{
                    position: "absolute",
                    right: "clamp(16px, 4vw, 48px)",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontFamily: "'Space Mono', monospace",
                    fontSize: "clamp(80px, 12vw, 160px)",
                    fontWeight: 900,
                    color: "#111",
                    opacity: 0,
                    userSelect: "none",
                    pointerEvents: "none",
                    lineHeight: 1,
                    zIndex: 0,
                }}
            >
                {service.num}
            </span>

            {/* ── Collapsed header row ──────────────────────────────────── */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "clamp(12px, 2vw, 24px)",
                    padding: "clamp(20px, 3vw, 28px) clamp(16px, 4vw, 40px)",
                    paddingLeft: "clamp(24px, 4vw, 48px)", // compensate for accent bar
                    minHeight: 80,
                    position: "relative",
                    zIndex: 2,
                }}
            >
                {/* Index label */}
                <span
                    style={{
                        fontFamily: "'Space Mono', monospace",
                        fontSize: "clamp(10px, 1.2vw, 13px)",
                        fontWeight: 700,
                        color: "#999",
                        letterSpacing: "0.1em",
                        flexShrink: 0,
                        display: "block",
                        minWidth: "2ch",
                    }}
                >
                    {service.num}
                </span>

                {/* Icon bubble */}
                <div
                    className="icon-box icon-box--service"
                    style={{
                        backgroundColor: isOpen ? service.accent : "#F2EFE9",
                        boxShadow: isOpen ? "3px 3px 0 #111" : "none",
                        marginBottom: 0,
                    }}
                >
                    {service.icon}
                </div>

                {/* Service name */}
                <span
                    style={{
                        fontFamily: "'Syne', sans-serif",
                        fontWeight: 800,
                        fontSize: "clamp(20px, 3.5vw, 42px)",
                        letterSpacing: "-0.03em",
                        color: "#111",
                        lineHeight: 1.05,
                        flex: 1,
                    }}
                >
                    {service.text}
                </span>

                {/* Arrow indicator */}
                <span
                    ref={arrowRef}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "clamp(32px, 4vw, 44px)",
                        height: "clamp(32px, 4vw, 44px)",
                        border: "2.5px solid #111",
                        borderRadius: "50%",
                        flexShrink: 0,
                        backgroundColor: "transparent",
                        transformOrigin: "center",
                    }}
                >
                    <svg
                        width="14" height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="#111"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M7 2v10M2 7l5 5 5-5" />
                    </svg>
                </span>
            </div>

            {/* ── Expandable body ───────────────────────────────────────── */}
            <div
                ref={bodyRef}
                style={{
                    height: 0,
                    opacity: 0,
                    overflow: "hidden",
                    position: "relative",
                    zIndex: 2,
                }}
            >
                <div
                    style={{
                        padding: "0 clamp(16px, 4vw, 40px) clamp(20px, 3vw, 32px)",
                        paddingLeft: "clamp(24px, 4vw, 48px)",
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: "clamp(16px, 3vw, 32px)",
                        alignItems: "flex-end",
                    }}
                >
                    {/* Description */}
                    <div>
                        {/* Accent divider */}
                        <div
                            style={{
                                width: 48,
                                height: 3,
                                backgroundColor: service.accent,
                                borderRadius: 2,
                                marginBottom: 14,
                                border: "1.5px solid #111",
                            }}
                        />
                        <p
                            style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: "clamp(14px, 1.5vw, 16px)",
                                fontWeight: 400,
                                lineHeight: 1.75,
                                color: "#444",
                                maxWidth: "65ch",
                                margin: 0,
                            }}
                        >
                            {service.desc}
                        </p>
                    </div>

                    {/* Tags */}
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            justifyContent: "flex-end",
                        }}
                    >
                        {service.tags.map((tag) => (
                            <span
                                key={tag}
                                style={{
                                    fontFamily: "'Space Mono', monospace",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: "0.1em",
                                    textTransform: "uppercase",
                                    padding: "5px 10px",
                                    border: "2px solid #111",
                                    borderRadius: 4,
                                    backgroundColor: service.accent,
                                    color: "#111",
                                    whiteSpace: "nowrap",
                                    boxShadow: "2px 2px 0 #111",
                                }}
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Section ───────────────────────────────────────────────────────────────────
const ServicesSection = () => {
    const containerRef = useRef(null);
    const [openIndex, setOpenIndex]   = useState(null); // accordion state

    const handleToggle = useCallback((idx) => {
        setOpenIndex((prev) => (prev === idx ? null : idx));
    }, []);

    // Section entrance animation (rows slide in from bottom)
    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.set([".svc-badge", ".svc-heading", ".svc-row"], { opacity: 0 });

            sectionBadge(".svc-badge");

            // Heading scramble reveal
            gsap.fromTo(
                ".svc-heading",
                { opacity: 0, y: 40 },
                {
                    opacity: 1, y: 0, duration: 0.8, ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".svc-heading",
                        start: "top 95%",
                        toggleActions: "play none none reverse",
                        fastScrollEnd: true,
                        onEnter: () => scrambleText(".svc-scramble", "What I Offer."),
                    },
                }
            );

            // Rows stagger in
            gsap.fromTo(
                ".svc-row",
                { opacity: 0, y: 30 },
                {
                    opacity: 1, y: 0,
                    stagger: 0.1,
                    duration: 0.6,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".svc-row",
                        start: "top 92%",
                        toggleActions: "play none none reverse",
                        fastScrollEnd: true,
                    },
                }
            );
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="services"
            ref={containerRef}
            className="py-24 font-jakarta bg-[#F2EFE9] overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6">

                {/* ── Header ──────────────────────────────────────────────── */}
                <div className="mb-12">
                    <div className="svc-badge inline-flex items-center gap-2 px-4 py-2 bg-[#1C1C1E] text-white border-2 border-[#111] rounded-full font-space font-bold text-sm shadow-brutal-sm mb-5">
                        <span className="w-2 h-2 rounded-full bg-[#FF3366] inline-block" />
                        SERVICES
                    </div>
                    <h2
                        className="svc-heading text-4xl md:text-5xl lg:text-6xl font-black text-[#111] leading-tight"
                        style={{ fontFamily: "'Syne', sans-serif" }}
                    >
                        What I{" "}
                        <span className="svc-scramble text-[#FF3366]">Offer.</span>
                    </h2>
                </div>

                {/* ── Accordion rows ──────────────────────────────────────── */}
                <div
                    style={{
                        border: "2.5px solid #111",
                        borderBottom: "none",        // each row adds its own border-bottom
                        borderRadius: 16,
                        overflow: "hidden",
                        boxShadow: "8px 8px 0 #111",
                        backgroundColor: "#F2EFE9",
                    }}
                >
                    {servicesData.map((service, idx) => (
                        <ServiceRow
                            key={service.num}
                            service={service}
                            index={idx}
                            isOpen={openIndex === idx}
                            onToggle={() => handleToggle(idx)}
                        />
                    ))}
                </div>

                {/* ── Sub-caption ─────────────────────────────────────────── */}
                <p
                    style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 13,
                        color: "#999",
                        marginTop: 20,
                        letterSpacing: "0.04em",
                        textAlign: "right",
                    }}
                >
                    ↑ Click any service to expand
                </p>

            </div>
        </section>
    );
};

export default ServicesSection;
